export const GOLDEN_ENROLMENT_FEE_CENTS = 1_000_000;
/** Sales comp change, 27 October 2026 (rulings 1 and 2): Standard earns 3.00% on every qualifying charger, with no rate steps;
 * Golden earns 3.50% at lifetime qualifying chargers 1 to 49, 4.00% at 50 to 99, 4.50% at 100 to 149 and 5.00% from 150. */
export const SP_STANDARD_BPS = 300;
export const SP_GOLDEN_MIN_BPS = 350;
export const SP_GOLDEN_MAX_BPS = 500;
export const GOLDEN_TIER_BOUNDARIES = [50, 100, 150];
/** Standard has no rate steps since 27 October 2026. Qualification at the fourth charger is tracked separately. */
export const STANDARD_TIER_BOUNDARIES = [];
export function hasPaidGoldenEnrolment(a) {
    return !!a.golden_enrolled_at && Number.isFinite(+new Date(a.golden_enrolled_at))
        && /^[a-f\d]{24}$/i.test(String(a.golden_enrolled_by ?? ''))
        && a.golden_enrolment_fee_cents === GOLDEN_ENROLMENT_FEE_CENTS
        && a.golden_enrolment_currency === 'USD';
}
export function spCommissionBps(ordinal, tier = 'standard') {
    if (!Number.isSafeInteger(ordinal) || ordinal < 1)
        throw new Error('Deployment ordinal must be a positive integer');
    if (tier !== 'standard' && tier !== 'golden')
        throw new Error('Unknown commission tier');
    if (tier === 'golden') {
        if (ordinal >= 150)
            return SP_GOLDEN_MAX_BPS;
        if (ordinal >= 100)
            return 450;
        if (ordinal >= 50)
            return 400;
        return SP_GOLDEN_MIN_BPS;
    }
    return SP_STANDARD_BPS;
}
export function spLaunchStamp(agent, ordinal, launchedAt) {
    if (!Number.isFinite(+launchedAt))
        throw new Error('Invalid launch time');
    const tier = spProgramAt(agent, launchedAt);
    return { agent_commission_bps: spCommissionBps(ordinal, tier), agent_commission_tier: tier,
        agent_deployment_ordinal: ordinal, agent_commission_stamped_at: launchedAt };
}
/** History is authoritative. Legacy paid evidence is the initial interval,
 * never a mutable current-program field and never a backfill of stamps. */
export function spProgramAt(agent, instant) {
    if (!Number.isFinite(+instant))
        throw new Error('Invalid program instant');
    let program = hasPaidGoldenEnrolment(agent)
        && +new Date(agent.golden_enrolled_at) <= +instant ? 'golden' : 'standard';
    for (const entry of agent.program_history ?? []) {
        if (+new Date(entry.effective_at) <= +instant)
            program = entry.new_program;
    }
    return program;
}
export function spNaturalQualifiedAt(agent) {
    if (agent.natural_qualified_at)
        return new Date(agent.natural_qualified_at);
    const fourth = agent.tier_history?.find(h => h.boundary === 4);
    if (fourth)
        return new Date(fourth.at);
    // Old paid enrolment overloaded qualified_at. Do not turn that waiver into
    // permanent natural qualification when the Golden interval ends.
    if (hasPaidGoldenEnrolment(agent) && agent.qualified_at
        && +new Date(agent.qualified_at) === +new Date(agent.golden_enrolled_at))
        return null;
    return agent.qualified_at ? new Date(agent.qualified_at) : null;
}
export function spEligibleAt(agent, instant) {
    const natural = spNaturalQualifiedAt(agent);
    return (!!natural && +natural <= +instant) || spProgramAt(agent, instant) === 'golden';
}
export function spProgramView(agent, now = new Date()) {
    const program = spProgramAt(agent, now);
    const boundaries = program === 'golden' ? GOLDEN_TIER_BOUNDARIES : STANDARD_TIER_BOUNDARIES;
    const count = agent.deployed_paid_count ?? 0;
    const ladder = spLadder(program);
    const next = boundaries.find(b => b > count);
    const pending = (agent.program_history ?? []).filter(e => +new Date(e.effective_at) > +now)
        .map(e => ({ program: e.new_program, effective_at: new Date(e.effective_at).toISOString() }));
    return { program, name: program === 'golden' ? 'Golden Partner' : 'Sales Partner',
        effective_status: pending.length ? 'scheduled' : 'current', pending,
        ladder, cap_bps: program === 'golden' ? SP_GOLDEN_MAX_BPS : SP_STANDARD_BPS,
        current_rate_bps: spCommissionBps(Math.max(1, count), program),
        // The rate the next qualifying launch is stamped with (the launch raises the count first), which differs from
        // current_rate_bps at a ladder boundary: a Golden partner at 49 stamps the 50th at 4.00%.
        next_launch_rate_bps: spCommissionBps(count + 1, program),
        next_tier: next ? { count: next, rate_bps: spCommissionBps(next, program) } : null,
        eligible: spEligibleAt(agent, now), natural_qualified_at: spNaturalQualifiedAt(agent)?.toISOString() ?? null };
}
export function spLadder(program) {
    const boundaries = program === 'golden' ? GOLDEN_TIER_BOUNDARIES : STANDARD_TIER_BOUNDARIES;
    return [1, ...boundaries].map(from => ({ from, rate_bps: spCommissionBps(from, program) }));
}
export function validSpRate(bps, golden) {
    return Number.isSafeInteger(bps) && bps >= 0 && bps <= (golden ? 500 : 300);
}
//# sourceMappingURL=spTier.js.map